import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  RollbackOutlined,
  AppstoreAddOutlined,
  ApiOutlined,
  MoneyCollectOutlined,
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  WalletOutlined,
  AccountBookOutlined,
  IdcardOutlined,
  ShopOutlined,
  AppstoreOutlined,
  DollarOutlined,
  ToolOutlined,
  FileTextOutlined,
  BuildOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { RESOURCES } from '../../utils/rolePermissions';
import Logo from './Logo';

const { Header, Sider, Content } = Layout;

const MainLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { canAccess, isAdmin } = usePermissions();

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileDrawerOpen(false);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-open submenu when navigating to its pages
  useEffect(() => {
    const operationsPaths = ['/inventory', '/sales', '/returns', '/purchases'];
    const catalogPaths = ['/products', '/platforms', '/pricing'];
    const financePaths = ['/supplier-payments', '/expenses'];
    const manufacturingPaths = ['/manufacturing', '/challan'];
    
    if (operationsPaths.includes(location.pathname)) {
      setOpenKeys(['operations']);
    } else if (catalogPaths.includes(location.pathname)) {
      setOpenKeys(['catalog']);
    } else if (financePaths.includes(location.pathname)) {
      setOpenKeys(['finance']);
    } else if (manufacturingPaths.includes(location.pathname)) {
      setOpenKeys(['manufacturing']);
    }
  }, [location.pathname]);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      resource: RESOURCES.DASHBOARD,
    },
    {
      key: 'catalog',
      icon: <AppstoreOutlined />,
      label: 'Catalog & Pricing',
      children: [
        {
          key: '/products',
          icon: <ShoppingOutlined />,
          label: 'Products',
          resource: RESOURCES.PRODUCTS,
        },
        {
          key: '/platforms',
          icon: <ApiOutlined />,
          label: 'Platforms',
          resource: RESOURCES.PLATFORMS,
        },
        {
          key: '/pricing',
          icon: <MoneyCollectOutlined />,
          label: 'Pricing (INR)',
          resource: RESOURCES.PRICING,
        },
      ],
    },
    {
      key: 'operations',
      icon: <ShopOutlined />,
      label: 'Operations',
      children: [
        {
          key: '/inventory',
          icon: <InboxOutlined />,
          label: 'Inventory',
          resource: RESOURCES.INVENTORY,
        },
        {
          key: '/sales',
          icon: <ShoppingCartOutlined />,
          label: 'Sales',
          resource: RESOURCES.SALES,
        },
        {
          key: '/returns',
          icon: <RollbackOutlined />,
          label: 'Returns',
          resource: RESOURCES.RETURNS,
        },
        {
          key: '/purchases',
          icon: <AppstoreAddOutlined />,
          label: 'Purchases',
          resource: RESOURCES.PURCHASES,
        },
      ],
    },
    {
      key: 'finance',
      icon: <DollarOutlined />,
      label: 'Finance',
      // Show if user can access at least one finance resource
      hidden: !canAccess(RESOURCES.EXPENSES) && !canAccess(RESOURCES.SUPPLIER_PAYMENTS),
      children: [
        {
          key: '/supplier-payments',
          icon: <WalletOutlined />,
          label: 'Supplier Payments',
          resource: RESOURCES.SUPPLIER_PAYMENTS,
        },
        {
          key: '/expenses',
          icon: <AccountBookOutlined />,
          label: 'Expenses',
          resource: RESOURCES.EXPENSES,
        },
      ],
    },
    {
      key: 'manufacturing',
      icon: <ToolOutlined />,
      label: 'Manufacturing',
      children: [
        {
          key: '/manufacturing',
          icon: <BuildOutlined />,
          label: 'Lot Management',
          resource: RESOURCES.MANUFACTURING,
        },
        {
          key: '/challan',
          icon: <FileTextOutlined />,
          label: 'Challan',
          resource: RESOURCES.CHALLAN,
        },
      ],
    },
    {
      key: '/employees',
      icon: <IdcardOutlined />,
      label: 'Employees',
      resource: RESOURCES.EMPLOYEES,
    },
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: 'Users',
      resource: RESOURCES.USERS,
    },
  ];

  const filteredMenuItems = menuItems
    .filter(item => {
      // Check if item is hidden
      if (item.hidden) return false;
      
      // Check admin-only items
      if (item.admin && !isAdmin()) return false;
      
      // Check resource-based access
      if (item.resource && !canAccess(item.resource)) return false;
      
      return true;
    })
    .map(item => {
      // Filter children if they exist
      if (item.children) {
        const filteredChildren = item.children.filter(child => {
          // Check admin-only children
          if (child.admin && !isAdmin()) return false;
          
          // Check resource-based access for children
          if (child.resource && !canAccess(child.resource)) return false;
          
          return true;
        });
        
        // Only return parent if it has children after filtering
        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren
          };
        }
        return null;
      }
      return item;
    })
    .filter(item => item !== null); // Remove items with no children

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const handleMenuClick = (key) => {
    navigate(key);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const menuContent = (
    <>
      <Logo collapsed={collapsed} isMobile={isMobile} />
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={filteredMenuItems}
        onClick={({ key }) => handleMenuClick(key)}
        style={{ borderRight: 0 }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="dark"
          breakpoint="lg"
          width={240}
          collapsedWidth={80}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            height: '100vh',
            overflow: 'auto',
            zIndex: 100,
          }}
        >
          {menuContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          bodyStyle={{ padding: 0, background: '#001529' }}
          headerStyle={{ display: 'none' }}
          width={280}
        >
          {menuContent}
        </Drawer>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 240,
          transition: 'margin-left 0.2s',
        }}
      >
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            height: 64,
          }}
        >
          <Button
            type="text"
            icon={
              isMobile ? (
                <MenuUnfoldOutlined />
              ) : collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )
            }
            onClick={() => {
              if (isMobile) {
                setMobileDrawerOpen(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            style={{
              fontSize: '16px',
              width: isMobile ? 48 : 64,
              height: 64,
            }}
          />
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            overlayStyle={{ minWidth: '120px' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                cursor: 'pointer',
                padding: isMobile ? '4px 8px' : '8px 12px',
                borderRadius: '4px',
              }}
            >
              <Avatar
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1890ff' }}
                size={isMobile ? 'default' : 'large'}
              />
              {!isMobile && (
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#262626',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.username}
                </div>
              )}
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            background: '#f0f2f5',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
